import * as Yup from 'yup';
import { AppDispatch, RootState } from '@/app/store';
import { Button, Grid, Theme, Typography } from '@mui/material';
import { CreateFormProps } from '@/types/create/CreateFormProps';
import { FixedButton } from '@/components/common/button/FixedButton';
import { Form, Formik, FormikProps } from 'formik';
import { Helmet } from 'react-helmet-async';
import { MetaType } from '@/types/MetaDTO';
import { MobileBar } from '@/components/home/MobileBar';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { OutlinedField } from '@/components/common/field/OutlinedField';
import { PageHeader } from '@/components/common/PageHeader';
import { cleanLetterNumberInput } from '@/utils/string-utils';
import { emailSchema } from '@/validation/emailSchema';
import { letterNumberSchema } from '@/validation/letterNumberSchema';
import { setLetterInfo } from '@/services/departmentRedirectFormSlice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetMetaDataQuery } from '@/services/api/metaApiSlice';
import React, { ChangeEvent, FC, ReactElement } from 'react';

interface FormValues {
  letterNumber: string;
  email: string;
}

const validationSchema = Yup.object({
  letterNumber: letterNumberSchema,
  email: emailSchema,
});

export const DepartmentChangeFormLetter: FC<CreateFormProps> = (props: CreateFormProps): ReactElement => {
  const { tittle, nextFormPath, previousFormPath, previousFormLabel, isMobile, mobileTittle } = props;

  const dispatch: AppDispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();

  const departmentRedirectFormReducer = useAppSelector((state: RootState) => {
    return state.departmentRedirectForm;
  });

  const { data: meta } = useGetMetaDataQuery(MetaType.REDIRECT_LETTER);

  const initialValues: FormValues = {
    letterNumber: departmentRedirectFormReducer.letterNumber?.toString() || '',
    email: departmentRedirectFormReducer.email || '',
  };

  const handleSubmit = (value: FormValues) => {
    const cleanedLetterNumber: string = cleanLetterNumberInput(value.letterNumber);
    const letterNumberValue: number | undefined =
      cleanedLetterNumber !== '' ? parseInt(cleanedLetterNumber, 10) : undefined;

    dispatch(
      setLetterInfo({
        letterNumber: letterNumberValue,
        email: value.email,
      }),
    );
    if (nextFormPath) {
      navigate(nextFormPath);
    }
  };
  return (
    <>
      {meta && (
        <Helmet>
          <title>{meta.title}</title>
          <meta name={'description'} content={meta.description} />
          <meta name={'keywords'} content={meta.keywords} />
        </Helmet>
      )}
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        validateOnChange={true}
        onSubmit={handleSubmit}>
        {(formikProps: FormikProps<FormValues>) => {
          const { values, errors, touched, submitForm, setFieldValue, setFieldTouched } = formikProps;
          const handleSetFieldValue = (field: string, value: unknown) => {
            setFieldTouched(field, true, false);
            setFieldValue(field, value, true);
          };
          return (
            <Form>
              <Grid
                container={true}
                direction={'column'}
                sx={{
                  paddingBottom: (theme: Theme) => (isMobile ? theme.spacing(6) : theme.spacing(0)),
                }}>
                <Grid item={true}>
                  <Typography
                    fontSize={'14px'}
                    lineHeight={'20px'}
                    fontWeight={'500'}
                    align={'center'}
                    marginBottom={'15px'}
                    sx={{ color: (theme: Theme) => theme.colors.grayText }}>
                    {'Перенаправить письмо в другое учреждение'}
                  </Typography>
                </Grid>
                <Grid item={true} marginBottom={isMobile ? '48px' : '79px'}>
                  <PageHeader
                    tittle={tittle}
                    previousFormLabel={previousFormLabel}
                    previousFormPath={previousFormPath}
                    isMobile={isMobile}
                    mobileTittle={mobileTittle}
                  />
                </Grid>
                <Grid item={true}>
                  <Grid container={true} spacing={isMobile ? 6 : 3}>
                    <Grid item={true} xs={isMobile ? 12 : 4}>
                      <OutlinedField
                        fullWidth={true}
                        label={'Номер письма'}
                        name={'letterNumber'}
                        value={values.letterNumber}
                        error={touched.letterNumber && Boolean(errors.letterNumber)}
                        helperText={touched.letterNumber ? errors.letterNumber : ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleSetFieldValue(e.target.name, cleanLetterNumberInput(e.target.value));
                        }}
                      />
                    </Grid>
                    <Grid item={true} xs={true}>
                      <OutlinedField
                        fullWidth={true}
                        label={'Электронная почта отправителя'}
                        name={'email'}
                        value={values.email}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email ? errors.email : ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleSetFieldValue(e.target.name, e.target.value);
                        }}
                      />
                    </Grid>
                    {isMobile ? (
                      <MobileBar>
                        <FixedButton
                          disabled={!(values.letterNumber && values.email)}
                          color={'primary'}
                          variant={'contained'}
                          onClick={submitForm}>
                          {'Продолжить'}
                        </FixedButton>
                      </MobileBar>
                    ) : (
                      <Grid item={true} xs={true}>
                        <Button
                          disabled={!(values.letterNumber && values.email)}
                          onClick={submitForm}
                          variant={'contained'}
                          color={'primary'}
                          fullWidth={true}
                          size={'medium'}>
                          {'Продолжить'}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
